import { type Dispatch, useCallback, useEffect } from 'react';

interface AppEvent<PayloadType = unknown> extends Event {
  detail: PayloadType;
}

export interface QueueEventPayload {
  field: string;
  duration: number;
  message?: string;
}

export interface CustomWindowEventMap extends WindowEventMap {
  onQueueRequestResolve: AppEvent<QueueEventPayload>;
  onQueueRequestChangeStatus: AppEvent<QueueEventPayload>;
  onRequestToInProgress: AppEvent<QueueEventPayload>;
}

export const useEvent = <PayloadType = unknown>(
  eventName: keyof CustomWindowEventMap,
  callback?: Dispatch<PayloadType> | VoidFunction
) => {
  useEffect(() => {
    if (!callback) {
      return;
    }

    const listener = ((event: AppEvent<PayloadType>) => {
      callback(event.detail); // Use `event.detail` for custom payloads
    }) as EventListener;

    window.addEventListener(eventName, listener);
    return () => {
      window.removeEventListener(eventName, listener);
    };
  }, [callback, eventName]);

  const dispatch = useCallback(
    (detail: PayloadType) => {
      const event = new CustomEvent(eventName, { detail });
      window.dispatchEvent(event);
    },
    [eventName]
  );

  return { dispatch };
};
